import React, { useState } from 'react';
import useLanguage from '../../hooks/useLanguage';
import contactData from '../../data/contact.json';
import { validateContactForm } from '../../utils/validation';
import Button from '../common/Button';
import { FiSend, FiCheckCircle } from 'react-icons/fi';

/**
 * ContactForm Component
 * Contact form with validation and state management
 */
const ContactForm = () => {
  const { language } = useLanguage();
  const { inquiryTypes, formFields, privacyPolicy, successMessage } = contactData;

  // Initial form state
  const initialFormData = {
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    message: '',
    privacy: false
  };

  // State management
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateContactForm(formData, language);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Simulate form submission
    setIsSubmitting(true);

    // Simulate API call delay
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData(initialFormData);
        setShowSuccess(false);
      }, 3000);
    }, 1000);
  };

  return (
    <div className="space-y-6">
      {/* Section Title */}
      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          {language === 'ko' ? '문의하기' : 'Send Inquiry'}
        </h2>
        <p className="text-gray-600">
          {language === 'ko'
            ? '궁금하신 사항을 남겨주시면 빠르게 답변드리겠습니다.'
            : 'Please leave your inquiry and we will respond promptly.'}
        </p>
      </div>

      {/* Success Message */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
          <FiCheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-green-900">
              {language === 'ko' ? successMessage.title : successMessage.titleEn}
            </h3>
            <p className="text-sm text-green-700 mt-1">
              {language === 'ko' ? successMessage.description : successMessage.descriptionEn}
            </p>
          </div>
        </div>
      )}

      {/* Contact Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
            {language === 'ko' ? formFields.name.label : formFields.name.labelEn}
            {formFields.name.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={language === 'ko' ? formFields.name.placeholder : formFields.name.placeholderEn}
            className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${
              errors.name
                ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
            }`}
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
            {language === 'ko' ? formFields.email.label : formFields.email.labelEn}
            {formFields.email.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={language === 'ko' ? formFields.email.placeholder : formFields.email.placeholderEn}
            className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${
              errors.email
                ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
            }`}
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        {/* Phone Field */}
        <div>
          <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
            {language === 'ko' ? formFields.phone.label : formFields.phone.labelEn}
            {formFields.phone.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder={language === 'ko' ? formFields.phone.placeholder : formFields.phone.placeholderEn}
            className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${
              errors.phone
                ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
            }`}
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>

        {/* Company Field */}
        <div>
          <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
            {language === 'ko' ? formFields.company.label : formFields.company.labelEn}
            {formFields.company.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            placeholder={language === 'ko' ? formFields.company.placeholder : formFields.company.placeholderEn}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
          />
        </div>

        {/* Inquiry Type Field */}
        <div>
          <label htmlFor="inquiryType" className="block text-sm font-semibold text-gray-700 mb-2">
            {language === 'ko' ? formFields.inquiryType.label : formFields.inquiryType.labelEn}
            {formFields.inquiryType.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <select
            id="inquiryType"
            name="inquiryType"
            value={formData.inquiryType}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${
              errors.inquiryType
                ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
            }`}
          >
            <option value="">
              {language === 'ko' ? formFields.inquiryType.placeholder : formFields.inquiryType.placeholderEn}
            </option>
            {inquiryTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {language === 'ko' ? type.label : type.labelEn}
              </option>
            ))}
          </select>
          {errors.inquiryType && <p className="text-red-500 text-sm mt-1">{errors.inquiryType}</p>}
        </div>

        {/* Message Field */}
        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
            {language === 'ko' ? formFields.message.label : formFields.message.labelEn}
            {formFields.message.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows="6"
            placeholder={language === 'ko' ? formFields.message.placeholder : formFields.message.placeholderEn}
            className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 transition-all resize-none ${
              errors.message
                ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
            }`}
          ></textarea>
          {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
        </div>

        {/* Privacy Policy Checkbox */}
        <div>
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              name="privacy"
              checked={formData.privacy}
              onChange={handleChange}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-200 mt-0.5"
            />
            <span className="text-sm text-gray-700">
              {language === 'ko' ? formFields.privacy.label : formFields.privacy.labelEn}
              <span className="text-red-500 ml-1">*</span>
            </span>
          </label>
          {errors.privacy && <p className="text-red-500 text-sm mt-1">{errors.privacy}</p>}

          {/* Privacy Policy Text */}
          <div className="mt-2 p-3 bg-gray-50 rounded-md">
            <p className="text-xs text-gray-600">
              {language === 'ko' ? privacyPolicy.text : privacyPolicy.textEn}
            </p>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full flex items-center justify-center space-x-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>{language === 'ko' ? '전송 중...' : 'Sending...'}</span>
              </>
            ) : (
              <>
                <FiSend className="w-5 h-5" />
                <span>{language === 'ko' ? '문의 보내기' : 'Send Inquiry'}</span>
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
